/**
 * Tipos e helpers do painel "Aplicar Simulação".
 * A gravação é um único POST /aplicacao; este módulo guarda só o modelo do painel.
 */

import { chaveAlteracao } from "./simulacao.model";
import type { AlteracaoSimulada, CenarioSimulacao } from "./simulacao.model";

export type EstadoAplicacao =
  "pendente" | "aplicando" | "aplicada" | "aplicada-com-aviso" | "nao-aplicada" | "descartada";

export interface ItemAplicacao {
  chave: string;
  /** Como foi simulada. */
  alteracao: AlteracaoSimulada;
  estado: EstadoAplicacao;
  mensagem: string | null;
  /** Por que não foi gravada: define a ação de reenvio possível. */
  motivo: MotivoFalha | null;
}

/**
 * "sistema" (5xx): "Tentar novamente". "conexao" (sem resposta/timeout): "Retomar".
 * "negocio" (recusa da regra): só se corrige em "Ajustar na simulação".
 */
export type MotivoFalha = "sistema" | "conexao" | "negocio";

/** Ordem de exibição: equipes primeiro, depois remanejamentos e ignorados. */
const ORDEM: Record<AlteracaoSimulada["acao"], number> = {
  "definir-equipe": 0,
  "remanejar-profissional": 1,
  "remanejar-fornecedor": 1,
  "remanejar-local": 1,
  "ignorar-conflito-profissional": 2,
  "ignorar-conflito-fornecedor": 2,
  "ignorar-conflito-local": 2,
};

export function ordenarParaExibicao(alteracoes: readonly AlteracaoSimulada[]): AlteracaoSimulada[] {
  return [...alteracoes]
    .map((alteracao, index) => ({ alteracao, index }))
    .sort((a, b) => ORDEM[a.alteracao.acao] - ORDEM[b.alteracao.acao] || a.index - b.index)
    .map(({ alteracao }) => alteracao);
}

/** Monta a lista inicial de itens do painel a partir do cenário (todos como "pendente"). */
export function itensDaAplicacao(cenario: CenarioSimulacao): ItemAplicacao[] {
  return ordenarParaExibicao(cenario.alteracoes).map((alteracao) => ({
    chave: chaveAlteracao(alteracao),
    alteracao,
    estado: "pendente",
    mensagem: null,
    motivo: null,
  }));
}

export const ESTADOS_FINAIS_OK: readonly EstadoAplicacao[] = ["aplicada", "aplicada-com-aviso"];

export function isAplicada(item: Pick<ItemAplicacao, "estado">): boolean {
  return ESTADOS_FINAIS_OK.includes(item.estado);
}
